/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/usePadFocusAdvance.ts
 * FILE_ID: QB-AGGRID-PAD-FOCUS-HOOK-001
 * PURPOSE: Hook that owns the "advance focus to the next blank pad
 *          row after promotion" behaviour. Triggered by the caller
 *          when a pad value change is detected, fires after the
 *          real-row count grows on the next render.
 * WHY: AG-Grid's native Enter/Tab advances by row id. After a pad
 *      promotion + backend refetch, the row id the user "advanced to"
 *      now sits one row lower than the SAM-expected target. This
 *      hook re-asserts focus onto the first pad row at the column
 *      the user was last editing.
 * FLOW:
 *   ┌───────────────────┐   ┌──────────────────────┐   ┌──────────────────────┐
 *   │ user commits a    │ → │ markPendingAdvance   │ → │ rows ref / count     │
 *   │ pad cell          │   │ (colId)              │   │ changes              │
 *   └───────────────────┘   └──────────────────────┘   └──────────────────────┘
 *                                                            │
 *                                              ┌─────────────┘
 *                                              ▼
 *                                  ┌──────────────────────────┐
 *                                  │ setFocusedCell(pad0,     │
 *                                  │   colId) +               │
 *                                  │ ensureIndexVisible       │
 *                                  └──────────────────────────┘
 * RELATED:
 *   - pad-focus-helpers.ts — pure findFirstPadRowIndex
 *   - lazy-aggrid-impl.tsx — consumer
 */

import { useEffect, useRef } from 'react'
import type { MutableRefObject } from 'react'
import type { QuoteLineItem } from '@/lib/quoteApi'
import { findFirstPadRowIndex } from './pad-focus-helpers'

/**
 * Minimal subset of AG-Grid's GridApi we touch. Kept narrow so the
 * hook stays unit-testable without pulling in ag-grid-community.
 */
export interface PadFocusGridApi {
  setFocusedCell: (rowIndex: number, colKey: string) => void
  ensureIndexVisible?: (index: number) => void
}

export interface UsePadFocusAdvanceResult {
  /** Call from onCellValueChanged when the changed row was a pad. */
  markPendingAdvance: (colId: string) => void
}

/**
 * Watch the row array. When the caller has marked a pending advance
 * AND the row count has just grown (meaning the backend create
 * round-trip landed and produced a fresh real row), re-focus the
 * topmost pad at the column the user was editing.
 */
export function usePadFocusAdvance(
  rows: QuoteLineItem[],
  apiRef: MutableRefObject<PadFocusGridApi | null>,
): UsePadFocusAdvanceResult {
  const pendingColRef = useRef<string | null>(null)
  const prevCountRef = useRef<number>(rows.length)

  useEffect(() => {
    const pendingCol = pendingColRef.current
    const prevCount = prevCountRef.current
    prevCountRef.current = rows.length
    if (!pendingCol) return
    // Only advance when row count actually grew — guards against pad
    // value changes that the backend rejected (no row added, focus
    // should stay where AG-Grid's native handler put it).
    if (rows.length <= prevCount) return
    pendingColRef.current = null
    const api = apiRef.current
    if (!api) return
    const targetRowIndex = findFirstPadRowIndex(rows)
    if (targetRowIndex < 0) return
    try {
      api.setFocusedCell(targetRowIndex, pendingCol)
      api.ensureIndexVisible?.(targetRowIndex)
    } catch {
      // setFocusedCell may throw if the column was just hidden /
      // removed mid-flight. Treat as a soft no-op.
    }
  }, [rows, apiRef])

  return {
    markPendingAdvance: (colId: string) => {
      pendingColRef.current = colId
    },
  }
}
